import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { useSeoMeta } from '@/hooks/usePageTitle';
import { PublicShell } from '@/components/visual/PublicShell';
import { LocaleLink } from '@/components/LocaleLink';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Mail, Lock, User, Play } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage() {
  const { loginJWT, loginDemo, registerJWT, loginGoogle, user } = useAuth();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const rawFrom = location.state?.from || localePath('/calculator');
  // Validate redirect target — only allow internal paths, prevent open redirect
  const from = rawFrom.startsWith('/') && !rawFrom.startsWith('//') ? rawFrom : localePath('/calculator');

  useSeoMeta(
    'Sign In or Create Account — ISMS Calculator & ISO Guidance',
    'ISMS Calculator: Free ISO 27001 cost & effort estimator with ISO guidance. Sign in to view saved estimates or try the demo - no sign-up required.'
  );

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, navigate, from]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginJWT(loginEmail, loginPassword);
      toast.success(t('auth.welcomeBack'));
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerJWT(regEmail, regPassword, regName);
      toast.success(t('auth.accountCreated'));
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTryDemo = async () => {
    setDemoLoading(true);
    try {
      await loginDemo();
      toast.success(t('auth.welcomeDemo'));
      navigate(from, { replace: true });
    } catch {
      toast.error(t('auth.demoFailed'));
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <PublicShell>
      <main id="main-content" tabIndex={-1} className="auth-main">
          <div>
            <div className="auth-heading">
              <h1 data-testid="auth-page-title">
                {t('auth.welcome')}
              </h1>
              <p className="mt-2 text-sm text-[#66707A]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('visual.authIntro')}
              </p>
            </div>

            <Card className="bg-white border-[#DDE1E5] rounded-lg shadow-card">
              <CardContent className="pt-6">
                <Button
                  variant="outline"
                  className="w-full h-11 border-[#DDE1E5] bg-white hover:bg-[#F5F6F7] hover:border-[#527F19]/35 text-[#171A1D] transition-colors"
                  onClick={loginGoogle}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('auth.continueGoogle')}
                </Button>

                <div className="relative my-6">
                  <Separator className="bg-[#DDE1E5]" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-[#8A929B]">
                    {t('auth.orUseEmail')}
                  </span>
                </div>

                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-[#F5F6F7]">
                    <TabsTrigger value="login" data-testid="login-tab">{t('auth.signInTab')}</TabsTrigger>
                    <TabsTrigger value="register" data-testid="register-tab">{t('auth.registerTab')}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    <form onSubmit={handleLogin} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="login-email" className="text-[#66707A]">{t('auth.email')}</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A929B]" />
                          <Input id="login-email" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]" placeholder={t('auth.emailPlaceholder')} required data-testid="login-email-input" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="login-password" className="text-[#66707A]">{t('auth.password')}</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A929B]" />
                          <Input id="login-password" type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]" placeholder={t('auth.passwordPlaceholder')} required data-testid="login-password-input" />
                        </div>
                      </div>
                      <Button type="submit" className="w-full h-11 bg-[#527F19] hover:bg-[#3F6114] text-white shadow-card transition-colors" disabled={loading} data-testid="login-submit-btn">
                        {loading ? t('auth.signingIn') : t('auth.signIn')}
                      </Button>
                      <div className="text-center">
                        <LocaleLink to="/reset-password" className="text-sm text-[#8A929B] hover:text-[#527F19] transition-colors" data-testid="forgot-password-link">
                          {t('auth.forgotPassword')}
                        </LocaleLink>
                      </div>
                    </form>
                  </TabsContent>

                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="reg-name" className="text-[#66707A]">{t('auth.fullName')}</Label>
                        <div className="relative mt-1">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A929B]" />
                          <Input id="reg-name" type="text" value={regName} onChange={e => setRegName(e.target.value)} className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]" placeholder={t('auth.namePlaceholder')} required data-testid="register-name-input" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="reg-email" className="text-[#66707A]">{t('auth.email')}</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A929B]" />
                          <Input id="reg-email" type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]" placeholder={t('auth.emailPlaceholder')} required data-testid="register-email-input" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="reg-password" className="text-[#66707A]">{t('auth.password')}</Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A929B]" />
                          <Input id="reg-password" type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]" placeholder={t('auth.passwordRegPlaceholder')} minLength={8} required data-testid="register-password-input" />
                        </div>
                      </div>
                      <Button type="submit" className="w-full h-11 bg-[#527F19] hover:bg-[#3F6114] text-white shadow-card transition-colors" disabled={loading} data-testid="register-submit-btn">
                        {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handleTryDemo}
                disabled={demoLoading}
                data-testid="try-demo-btn"
                className="inline-flex items-center gap-1.5 text-sm text-[#66707A] hover:text-[#527F19] transition-colors disabled:opacity-60"
              >
                <Play className="w-3.5 h-3.5" />
                {demoLoading ? t('auth.loadingDemo') : t('auth.demoLink')}
              </button>
            </div>
            <p className="auth-anonymous-note" data-testid="auth-anonymous-note">
              {t('homepage.final.note')}{' '}
              <LocaleLink to="/calculator" className="text-status-info underline underline-offset-4" data-testid="auth-calculator-link">{t('homepage.primaryAction')}</LocaleLink>
            </p>
          </div>
      </main>
    </PublicShell>
  );
}
